import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import api from "../lib/api";
import ChatHeader from "./chat/ChatHeader";
import MessageList from "./chat/MessageList";
import MessageInput from "./chat/MessageInput";
import SuggestionChips from "./chat/SuggestionChips";

const SUGGESTIONS = [
  "What classes do you offer?",
  "What should I bring to my first class?",
  "Where are courses held?",
  "Do you offer rentals?",
];

const WELCOME_MESSAGE =
  "Welcome to Frontline Defense. I'm here to answer questions on classes, booking, what to bring, and more. How can I help?";
const FALLBACK_MESSAGE =
  "I'm having trouble responding right now. Please try again, or call us at +1 (804) 420-2533.";

function getSessionId() {
  let sid = localStorage.getItem("fld_chat_sid");
  if (!sid) {
    sid =
      "fld-" +
      Date.now().toString(36) +
      Math.random().toString(36).slice(2, 8);
    localStorage.setItem("fld_chat_sid", sid);
  }
  return sid;
}

let _mid = 0;
const makeId = () => `m-${Date.now()}-${++_mid}`;
const mkMsg = (role, content) => ({ id: makeId(), role, content });

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([mkMsg("assistant", WELCOME_MESSAGE)]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sidRef = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, mkMsg("user", msg)]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: sidRef.current,
        message: msg,
      });
      setMessages((m) => [...m, mkMsg("assistant", data.reply)]);
    } catch {
      setMessages((m) => [...m, mkMsg("assistant", FALLBACK_MESSAGE)]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open AI assistant"
        className="fixed bottom-20 right-6 z-[9999] w-14 h-14 rounded-full overflow-hidden bg-[#0A0A0A] border-2 border-[#7CFF00] shadow-[0_0_24px_rgba(124,255,0,0.55)] hover:scale-110 transition-transform"
      >
        {open ? (
          <X className="w-6 h-6 text-[#7CFF00] mx-auto" />
        ) : (
          <>
            <img
              src="/avatar-chatbot.jpg"
              alt="Frontline FAQ Assist"
              className="w-full h-full object-cover"
            />
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-[#7CFF00] rounded-full animate-ping" />
            <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 bg-[#7CFF00] rounded-full border border-[#0A0A0A]" />
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-40 right-6 z-[9999] w-[92vw] max-w-md bg-[#0A0A0A] border border-[#7CFF00]/40 shadow-[0_0_40px_rgba(124,255,0,0.18)]"
        >
          <ChatHeader onClose={() => setOpen(false)} />
          <MessageList ref={scrollRef} messages={messages} busy={busy} />
          {messages.length <= 1 && (
            <SuggestionChips suggestions={SUGGESTIONS} onPick={sendMessage} />
          )}
          <MessageInput
            value={input}
            onChange={setInput}
            onSubmit={sendMessage}
            busy={busy}
          />
        </div>
      )}
    </>
  );
}
